import React, { useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { CalendarDay as CalendarDayType } from '../../types';
import { formatCalendarDate, getCalendarDayTooltip, isMultiDayBooking, hasCheckIn, hasCheckOut } from '../../utils/calendar-utils';

interface CalendarDayProps {
  day: CalendarDayType;
  onClick: (date: Date) => void;
}

const CalendarDay: React.FC<CalendarDayProps> = ({ day, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = () => {
    onClick(day.date);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(day.date);
    }
  };

  const handleMouseEnter = () => {
    if (day.bookings.length > 0) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const getBookingIndicators = () => {
    if (day.bookings.length === 0) return null;

    // For single booking, show a bar with potential multi-day span styling
    if (day.bookings.length === 1) {
      const booking = day.bookings[0];
      const isMultiDay = isMultiDayBooking(booking);
      const isStart = hasCheckIn(booking, day.date);
      const isEnd = hasCheckOut(booking, day.date);
      
      let borderRadius = 0;
      let left = 0;
      let right = 0;
      
      if (isMultiDay) {
        if (isStart && isEnd) {
          left = 4; right = 4; borderRadius = 2;
        } else if (isStart) {
          left = 4; right = 0; borderRadius = 2;
        } else if (isEnd) {
          left = 0; right = 4; borderRadius = 2;
        } else {
          left = 0; right = 0; borderRadius = 0;
        }
      } else {
        left = 4; right = 4; borderRadius = 2;
      }

      const barStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: 4,
        left,
        right,
        height: 6,
        backgroundColor: booking.displayColor,
        borderRadius,
      };
      
      if (booking.isExternal) {
        barStyle.backgroundImage = 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)';
        barStyle.border = '1px solid rgba(255,255,255,0.5)';
      }

      return (
        <Box
          sx={barStyle}
          title={`${booking.listingTitle} - ${booking.status} (${booking.isExternal ? 'External' : 'Direct'})`}
        />
      );
    }

    // Multiple bookings - show stacked bars or dots based on space
    return (
      <Box sx={{ position: 'absolute', bottom: 4, left: 4, right: 4 }}>
        {day.bookings.length <= 3 ? (
          // Show stacked bars for up to 3 bookings
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {day.bookings.slice(0, 3).map((booking, index) => {
              const isMultiDay = isMultiDayBooking(booking);
              const isStart = hasCheckIn(booking, day.date);
              const isEnd = hasCheckOut(booking, day.date);
              
              let borderRadius = 0;
              if (isMultiDay) {
                if (isStart && isEnd) borderRadius = 1;
                else if (isStart) borderRadius = 1;
                else if (isEnd) borderRadius = 1;
              } else {
                borderRadius = 1;
              }

              const barStyle: React.CSSProperties = {
                height: 2,
                backgroundColor: booking.displayColor,
                borderRadius,
              };
              
              if (booking.isExternal) {
                barStyle.backgroundImage = 'repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(255,255,255,0.3) 1px, rgba(255,255,255,0.3) 2px)';
              }

              return (
                <Box
                  key={`${booking.bookingId}-${index}`}
                  sx={barStyle}
                />
              );
            })}
          </Box>
        ) : (
          // Show dots for many bookings
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
            {day.bookings.slice(0, 3).map((booking, index) => {
              const dotStyle: React.CSSProperties = {
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: booking.displayColor,
              };
              
              if (booking.isExternal) {
                dotStyle.border = '1px solid rgba(255,255,255,0.8)';
                dotStyle.boxShadow = '0 0 0 1px rgba(0,0,0,0.1)';
              }

              return (
                <Box
                  key={`${booking.bookingId}-${index}`}
                  sx={dotStyle}
                />
              );
            })}
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'grey.400' }} />
          </Box>
        )}
      </Box>
    );
  };

  const getCheckInOutIndicators = () => {
    const indicators = [];
    
    if (day.hasCheckIn) {
      indicators.push(
        <Box
          key="checkin"
          sx={{
            position: 'absolute',
            top: { xs: 2, sm: 4 },
            left: { xs: 2, sm: 4 },
            width: { xs: 6, sm: 8 },
            height: { xs: 6, sm: 8 },
            bgcolor: 'success.main',
            borderRadius: '50%',
            border: '1px solid white',
            boxShadow: 1,
            zIndex: 10,
          }}
          title="Check-in"
        />
      );
    }
    
    if (day.hasCheckOut) {
      indicators.push(
        <Box
          key="checkout"
          sx={{
            position: 'absolute',
            top: { xs: 2, sm: 4 },
            right: { xs: 2, sm: 4 },
            width: { xs: 6, sm: 8 },
            height: { xs: 6, sm: 8 },
            bgcolor: 'error.main',
            borderRadius: '50%',
            border: '1px solid white',
            boxShadow: 1,
            zIndex: 10,
          }}
          title="Check-out"
        />
      );
    }
    
    return indicators;
  };

  const getTooltipContent = () => {
    if (day.bookings.length === 0) return '';

    const lines = [
      `${formatCalendarDate(day.date)} ${day.date.toLocaleDateString('en-US', { month: 'short' })}`,
      ...day.bookings.slice(0, 3).map(booking => 
        `${booking.listingTitle} (${booking.status}) ${booking.isExternal ? '🔗' : ''}`
      )
    ];

    if (day.bookings.length > 3) {
      lines.push(`+${day.bookings.length - 3} more`);
    }

    return lines.join('\n');
  };

  const getBackgroundColor = () => {
    if (day.isSelected) return 'primary.light';
    if (day.isToday) return 'info.light';
    if (!day.isCurrentMonth) return 'grey.50';
    return 'background.paper';
  };

  const getBorderColor = () => {
    if (day.isSelected) return 'primary.main';
    if (day.isToday) return 'info.main';
    return 'divider';
  };

  const getTextColor = () => {
    if (day.isSelected) return 'primary.dark';
    if (day.isToday) return 'info.dark';
    if (!day.isCurrentMonth) return 'text.disabled';
    return 'text.primary';
  };

  const dayContent = (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 64, sm: 80 },
        minHeight: 44,
        minWidth: 44,
        bgcolor: getBackgroundColor(),
        border: 1,
        borderColor: getBorderColor(),
        cursor: 'pointer',
        transition: 'all 0.2s',
        color: getTextColor(),
        '&:hover': {
          bgcolor: day.bookings.length > 0 ? 'primary.lighter' : 'action.hover',
          boxShadow: day.bookings.length > 0 ? 1 : 0,
        },
        '&:focus': {
          outline: 'none',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      role="button"
      aria-label={getCalendarDayTooltip(day)}
    >
      {/* Date number */}
      <Typography
        variant="body2"
        sx={{
          position: 'absolute',
          top: { xs: 4, sm: 8 },
          left: { xs: 4, sm: 8 },
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          fontWeight: 500,
          zIndex: 10,
        }}
      >
        {formatCalendarDate(day.date)}
      </Typography>

      {/* Check-in/out indicators */}
      {getCheckInOutIndicators()}

      {/* Booking indicators */}
      {getBookingIndicators()}

      {/* Booking count for multiple bookings */}
      {day.bookings.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 4, sm: 8 },
            right: { xs: 4, sm: 8 },
            width: { xs: 12, sm: 16 },
            height: { xs: 12, sm: 16 },
            bgcolor: 'grey.600',
            color: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: { xs: '0.625rem', sm: '0.75rem' },
            zIndex: 10,
            boxShadow: 1,
          }}
        >
          {day.bookings.length}
        </Box>
      )}
    </Box>
  );

  return day.bookings.length > 0 ? (
    <Tooltip title={getTooltipContent()} placement="top" arrow>
      {dayContent}
    </Tooltip>
  ) : (
    dayContent
  );
};

export default CalendarDay;